import { createStyles, Menu } from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { openModal } from '../../ModalManager';

const useStyles = createStyles((theme) => ({
	button: {
		color: theme.other.getColor('text/secondary/default'),
		'&:hover': {
			color: theme.other.getColor('text/primary/default'),
		},
	},
	modalContent: {
		padding: theme.spacing.md,
	},
	usageItem: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: `${theme.spacing.xs} 0`,
		borderBottom: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		'&:last-child': {
			borderBottom: 'none',
		},
	},
	usageLabel: {
		fontWeight: 500,
		color: theme.other.getColor('text/primary/default'),
	},
	usageValue: {
		fontWeight: 600,
		color: theme.other.getColor('text/primary/default'),
	},
}));

interface TokenUsageButtonProps {
	modelName?: string | null;
	inputTokens?: number | null;
	outputTokens?: number | null;
	totalTokens?: number | null;
	cacheCreationInputTokens?: number | null;
	cacheReadInputTokens?: number | null;
}

export function TokenUsageButton({
	modelName,
	inputTokens,
	outputTokens,
	totalTokens,
	cacheCreationInputTokens,
	cacheReadInputTokens,
}: TokenUsageButtonProps) {
	const { classes } = useStyles();

	// Only show if we have any token data
	if (!inputTokens && !outputTokens && !totalTokens) {
		return null;
	}

	const handleOpenModal = () => {
		openModal({
			title: 'Token usage',
			size: 'sm',
			centered: true,
			children: (
				<div className={classes.modalContent}>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						{modelName && (
							<div className={classes.usageItem}>
								<Text className={classes.usageLabel}>Model name</Text>
								<Text className={classes.usageValue}>{modelName}</Text>
							</div>
						)}
						{inputTokens !== null && inputTokens !== undefined && (
							<div className={classes.usageItem}>
								<Text className={classes.usageLabel}>Input tokens</Text>
								<Text className={classes.usageValue}>
									{inputTokens.toLocaleString()}
								</Text>
							</div>
						)}
						{outputTokens !== null && outputTokens !== undefined && (
							<div className={classes.usageItem}>
								<Text className={classes.usageLabel}>Output tokens</Text>
								<Text className={classes.usageValue}>
									{outputTokens.toLocaleString()}
								</Text>
							</div>
						)}
						{cacheCreationInputTokens !== null &&
							cacheCreationInputTokens !== undefined && (
								<div className={classes.usageItem}>
									<Text className={classes.usageLabel}>
										Cache creation input tokens
									</Text>
									<Text className={classes.usageValue}>
										{cacheCreationInputTokens.toLocaleString()}
									</Text>
								</div>
							)}
						{cacheReadInputTokens !== null &&
							cacheReadInputTokens !== undefined && (
								<div className={classes.usageItem}>
									<Text className={classes.usageLabel}>
										Cache read input tokens
									</Text>
									<Text className={classes.usageValue}>
										{cacheReadInputTokens.toLocaleString()}
									</Text>
								</div>
							)}
						{totalTokens !== null && totalTokens !== undefined && (
							<div className={classes.usageItem}>
								<Text className={classes.usageLabel}>Total tokens</Text>
								<Text className={classes.usageValue}>
									{totalTokens.toLocaleString()}
								</Text>
							</div>
						)}
					</div>
				</div>
			),
		});
	};

	return (
		<Menu.Item
			className={classes.button}
			onClick={handleOpenModal}
			icon={<Icon name="listSearch" />}
		>
			View details
		</Menu.Item>
	);
}
